import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import commonService from "../common/commonService";
import { API_URLS, STATUS_CODES } from "../common/constants";
import { startLoading, stopLoading } from "./commonSlice";

// Store blobs outside of Redux state to avoid serialization issues
let contractBlobCache = null;
let annexureBlobCache = null;

// Helper function to get cached blob
export const getCachedContractBlob = () => contractBlobCache;
export const getCachedAnnexureBlob = () => annexureBlobCache;

const initialState = {
  errorMessage: "",
  error: null,
  isContractSigned: false,
  contractPdfStatus: null, // { loaded: boolean, size: number, type: string }
  annexurePdfStatus: null, // { loaded: boolean, size: number, type: string }
  isLoadingContract: false,
  isLoadingAnnexure: false,
};

export const signContract = createAsyncThunk(
  "sign/contract",
  async (data, { rejectWithValue, dispatch }) => {
    dispatch(startLoading());
    const res = await commonService.putDataRequest(
      API_URLS.SIGN_CONTRACT,
      data
    );
    dispatch(stopLoading());
    if (res.statusCode !== STATUS_CODES.SUCCESS) {
      toast.error(res.message);
      return rejectWithValue(res.message);
    }
    toast.success(res.message);
    return res.data;
  }
);

export const signAnnexure = createAsyncThunk(
  "sign/annexure",
  async (data, { rejectWithValue, dispatch }) => {
    dispatch(startLoading());
    const res = await commonService.putDataRequest(
      API_URLS.SIGN_ANNEXURE,
      data
    );
    dispatch(stopLoading());
    if (res.statusCode !== STATUS_CODES.SUCCESS) {
      toast.error(res.message);
      return rejectWithValue(res.message);
    }
    toast.success(res.message);
    return res.data;
  }
);

export const getContract = createAsyncThunk(
  "contract/getContract",
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(startLoading());
    try {
      const response = await commonService.getBlobRequest(API_URLS.GET_CONTRACT);
      console.log("🔍 Contract API response:", response);
      console.log("🔍 Response status:", response.status);
      console.log("🔍 Response data type:", typeof response.data);
      console.log("🔍 Response data instanceof Blob:", response.data instanceof Blob);
      console.log("🔍 Response data size:", response.data?.size);
      console.log("🔍 Response data type:", response.data?.type);

      dispatch(stopLoading());

      // For blob responses, check status differently
      if (response.status !== STATUS_CODES.SUCCESS) {
        toast.error("Failed to fetch contract");
        return rejectWithValue("Failed to fetch contract");
      }

      // Store blob in cache (outside Redux state)
      contractBlobCache = response.data;

      // Return metadata only (serializable)
      return {
        loaded: true,
        size: response.data.size,
        type: response.data.type,
        timestamp: Date.now()
      };
    } catch (error) {
      console.error("❌ Error in getContract:", error);
      dispatch(stopLoading());
      toast.error("Failed to fetch contract");
      return rejectWithValue(error.message);
    }
  }
);

export const getAnnexure = createAsyncThunk(
  "contract/getAnnexure",
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(startLoading());
    try {
      const response = await commonService.getBlobRequest(API_URLS.GET_ANNEXURE);
      dispatch(stopLoading());

      // For blob responses, check status differently
      if (response.status !== STATUS_CODES.SUCCESS) {
        toast.error("Failed to fetch annexure");
        return rejectWithValue("Failed to fetch annexure");
      }

      // Store blob in cache (outside Redux state)
      annexureBlobCache = response.data;

      // Return metadata only (serializable)
      return {
        loaded: true,
        size: response.data.size,
        type: response.data.type,
        timestamp: Date.now()
      };
    } catch (error) {
      dispatch(stopLoading());
      toast.error("Failed to fetch annexure");
      return rejectWithValue(error.message);
    }
  }
);

const contractSlice = createSlice({
  name: "data",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(signContract.pending, (state) => {
        return {
          ...state,
          error: null,
        };
      })
      .addCase(signContract.fulfilled, (state, action) => {
        return {
          ...state,
          error: false,
          isContractSigned: true,
        };
      })
      .addCase(signContract.rejected, (state, action) => {
        return {
          ...state,
          error: action.payload,
          isContractSigned: false,
        };
      })
      .addCase(getContract.pending, (state) => {
        return {
          ...state,
          isLoadingContract: true,
          error: null,
        };
      })
      .addCase(getContract.fulfilled, (state, action) => {
        return {
          ...state,
          isLoadingContract: false,
          contractPdfStatus: action.payload,
          error: null,
        };
      })
      .addCase(getContract.rejected, (state, action) => {
        return {
          ...state,
          isLoadingContract: false,
          error: action.payload,
        };
      })
      .addCase(getAnnexure.pending, (state) => {
        return {
          ...state,
          isLoadingAnnexure: true,
          error: null,
        };
      })
      .addCase(getAnnexure.fulfilled, (state, action) => {
        return {
          ...state,
          isLoadingAnnexure: false,
          annexurePdfStatus: action.payload,
          error: null,
        };
      })
      .addCase(getAnnexure.rejected, (state, action) => {
        return {
          ...state,
          isLoadingAnnexure: false,
          error: action.payload,
        };
      });
  },
});

const { reducer } = contractSlice;
export default reducer;
